import { Setting } from 'src/Redux/api/settingsApi'
import { Offer, OfferModel } from 'src/Redux/api/offersApi'

import { subtractVatFromPrice } from '../../Helpers/functions'

import { GetTotalRentOptions } from './useCalculator'
import { ModelCalculated, OfferModelUnit } from './useCalculator'

// Enums
export enum VatVisibilityEnum {
  VAT = 'vat',
  NO_VAT = 'novat',
  ALL = 'all',
}

// Types
export interface OfferFormData {
  clientId: string
  clientContactName?: string
  clientContactNamePosition?: string
  clientDivision: string
  fuelPrice: number
  personDeliveryPrice: number
  partDeliveryPrice: number
  personnelHouseholdServicesCost?: number
  accommodationPrice?: number
  foodPrice?: number
  createDate: string
  validityDaysCount: number
  minRentPeriod: number
  season: string
  dailyMedCheckCost?: number
  personalBenefits?: boolean
  vatVisibility: VatVisibilityEnum
}

// Function to determine default season based on current month
export const getDefaultSeason = (): string => {
  const currentMonth = new Date().getMonth() + 1 // getMonth() returns 0-11, so add 1
  // October (10) to March (3) is winter, otherwise summer
  return currentMonth >= 10 || currentMonth <= 3 ? 'winter' : 'summer'
}

// Format date for input field (yyyy-MM-dd)
export const formatDateForInput = (dateString?: string): string => {
  if (!dateString) return new Date().toISOString().split('T')[0]
  return new Date(dateString).toISOString().split('T')[0]
}

// Format date to yyyy-MM-dd format
export const formatDateToYYYYMMDD = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toISOString().split('T')[0]
}

// Extract setting value by name from settings list
export const getSettingValue = (
  settingsList: Setting[],
  settingName: string,
): string | Array<string> | number | undefined => {
  return settingsList?.find((item) => item.name === settingName)?.value
}

// Get default form values
export const getDefaultFormValues = (): OfferFormData => ({
  clientId: '',
  clientContactName: '',
  clientContactNamePosition: '',
  clientDivision: '',
  fuelPrice: 0,
  personDeliveryPrice: 0,
  partDeliveryPrice: 0,
  personnelHouseholdServicesCost: 0,
  accommodationPrice: 0,
  foodPrice: 0,
  createDate: new Date().toISOString().split('T')[0],
  validityDaysCount: 0,
  minRentPeriod: 0,
  season: getDefaultSeason(),
  dailyMedCheckCost: 0,
  personalBenefits: false,
  vatVisibility: VatVisibilityEnum.VAT,
})

// Transform offer data to form data
export const transformOfferToFormData = (offer: Offer): OfferFormData => ({
  clientId: offer.clientId.toString(),
  clientContactName: offer.clientContactName || '',
  clientContactNamePosition: offer.clientContactNamePosition || '',
  clientDivision: offer.clientDivision || '',
  fuelPrice: offer.fuelPrice || 0,
  personDeliveryPrice: offer.personDeliveryPrice || 0,
  partDeliveryPrice: offer.deliveryPrice || 0,
  personnelHouseholdServicesCost: offer.personnelHouseholdServicesCost || 0,
  accommodationPrice: offer.accommodationPrice || 0,
  foodPrice: offer.foodPrice || 0,
  createDate: formatDateForInput(offer.createDate),
  validityDaysCount: offer.validityDaysCount || 0,
  minRentPeriod: offer.minRentPeriod || 0,
  season: offer.season || getDefaultSeason(),
  dailyMedCheckCost: offer.dailyMedCheckCost || 0,
  personalBenefits: offer.personalBenefits || false,
  vatVisibility:
    (offer.vatVisibility as VatVisibilityEnum) || VatVisibilityEnum.VAT,
})

export const getOfferGalleryImages = (model: ModelCalculated): string[] => {
  const selectedToolIds = new Set(
    (model.units || []).map((unit) => unit.toolId ?? unit.tool?.id),
  )
  const toolImages = (model.modelTools || [])
    .filter((tool) => selectedToolIds.has(tool.id))
    .flatMap((tool) => tool.images || [])

  return Array.from(
    new Set(
      [
        model.imageUrl,
        ...(model.images?.map((image) => image.url) || []),
        ...toolImages,
      ].filter((url): url is string => Boolean(url)),
    ),
  )
}

export interface VatSwitches {
  withVat: boolean
  withoutVat: boolean
}

export const toVatSwitches = (
  vatVisibility?: VatVisibilityEnum | string,
): VatSwitches => ({
  withVat: vatVisibility !== VatVisibilityEnum.NO_VAT,
  withoutVat: vatVisibility !== VatVisibilityEnum.VAT,
})

export const fromVatSwitches = ({
  withVat,
  withoutVat,
}: VatSwitches): VatVisibilityEnum | null => {
  if (withVat && withoutVat) return VatVisibilityEnum.ALL
  if (withVat) return VatVisibilityEnum.VAT
  if (withoutVat) return VatVisibilityEnum.NO_VAT
  return null
}

export const getDefaultToolId = (
  modelTools?: Array<{ id: number; isDefault?: boolean }>,
): number | undefined => modelTools?.find((tool) => tool.isDefault)?.id

export const getUnitQuantity = (unit: OfferModelUnit): number =>
  Math.max(unit.quantity ?? 1, 1)

export const getUnitsQuantity = (units: OfferModelUnit[] = []): number =>
  units.reduce((sum, unit) => sum + getUnitQuantity(unit), 0)

export const resolveRentPrice = (
  specialPrice: number | null | undefined,
  baseRentalCost: number,
): number => (specialPrice && specialPrice > 0 ? specialPrice : baseRentalCost)

// Transform offer models to ModelCalculated
export const transformOfferModelsToModelCalculated = (
  offerModels: OfferModel[],
  getTotalRent: (options: GetTotalRentOptions) => number,
  offer: Offer,
  medicalExaminationCost: number,
): ModelCalculated[] => {
  return offerModels.map((om) => {
    // Обратная совместимость: навесное и смены переехали на уровень экземпляра.
    // Старые КП хранят их на модели (om.toolId / om.shiftsCount), а экземпляры
    // могут отсутствовать или не иметь своих значений — наследуем из модели.
    const normalizedUnits =
      om.units && om.units.length > 0
        ? om.units.map((unit) => ({
            ...unit,
            toolId: unit.toolId ?? om.toolId,
            shiftCount: unit.shiftCount ?? om.shiftsCount,
          }))
        : Array.from({ length: om.count || 1 }, () => ({
            departurePoint: '',
            workPoint: '',
            returnPoint: '',
            deliveryCost: undefined,
            returnCost: undefined,
            shiftCount: om.shiftsCount,
            toolId: om.toolId,
          }))

    return {
      baseRentalCost: om.model.baseRentalCost,
      id: om.model.id,
      imageUrl: om.model.imageUrl,
      images: om.model.images,
      selectedImageUrl: om.selectedImageUrl,
      newestEquipmentYear: om.model.newestEquipmentYear,
      name: om.model.name,
      description: om.model.description,
      totalRentPricePerHour: getTotalRent({
        model: om.model,
        fuelPrice: offer.fuelPrice || 0,
        deliveryPrice: offer.deliveryPrice || 0,
        personDeliveryPrice: offer.personDeliveryPrice || 0,
        accommodationPrice: offer.accommodationPrice || 0,
        foodPrice: offer.foodPrice || 0,
        personnelHouseholdServicesCost:
          offer.personnelHouseholdServicesCost || 0,
        medicalExaminationCost: medicalExaminationCost || 0,
        relocationCost: om.relocationCost,
        selectedToolPrice: om.model.modelTools?.find(
          (tool) => tool.id === om.toolId,
        )?.toolWorkPrice,
        season: offer.season || getDefaultSeason(),
      }),
      specialPrice: resolveRentPrice(om.specialPrice, om.model.baseRentalCost),
      shiftsCount: om.shiftsCount,
      route: om.route,
      relocationCost: om.relocationCost,
      units: normalizedUnits,
      offerCount: getUnitsQuantity(normalizedUnits),
      selectedTool: om.toolId,
      detalization: om.isDetalized,
      count: getUnitsQuantity(normalizedUnits),
      toolId: om.toolId,
      fuelEfficiencySummer: om.model.fuelEfficiencySummer,
      fuelEfficiencyWinter: om.model.fuelEfficiencyWinter,
      createdAt: om.model.createdAt,
      driveType: om.model.driveType,
      engineCapacity: om.model.engineCapacity,
      modelTools: om.model.modelTools || [],
      imgBase63: om.model.imgBase63,
      modelType: om.model.modelType,
      modelTypeId: om.model.modelType.id,
      modelTypeName: om.model.modelType.label,
    }
  })
}

// Поля экземпляра, которые редактирует пользователь. Вычисляемые
// (totalRentPricePerHour) исключены — они пересчитываются при любом
// изменении формы и давали бы ложное «есть изменения».
type UnitToCompare = Pick<
  OfferModelUnit,
  | 'departurePoint'
  | 'workPoint'
  | 'returnPoint'
  | 'deliveryCost'
  | 'returnCost'
  | 'quantity'
  | 'shiftCount'
  | 'toolId'
>

// Create items to compare for change detection (excluding calculated fields)
export const createItemsToCompare = (
  items: ModelCalculated[],
): Array<{
  id: number
  specialPrice: number | undefined
  shiftsCount: number | undefined
  route: string | undefined
  relocationCost: number | undefined
  offerCount: number | undefined
  selectedTool: number | undefined
  detalization: boolean | undefined
  count: number | undefined
  selectedImageUrl: string | undefined
  units: UnitToCompare[]
}> => {
  return items.map((item) => ({
    id: item.id,
    specialPrice: item.specialPrice,
    shiftsCount: item.shiftsCount,
    route: item.route,
    relocationCost: item.relocationCost,
    offerCount: item.offerCount,
    selectedTool: item.selectedTool,
    detalization: item.detalization,
    count: item.count,
    selectedImageUrl: item.selectedImageUrl,
    units: (item.units || []).map((unit) => ({
      departurePoint: unit.departurePoint,
      workPoint: unit.workPoint,
      returnPoint: unit.returnPoint,
      deliveryCost: unit.deliveryCost,
      returnCost: unit.returnCost,
      quantity: unit.quantity,
      shiftCount: unit.shiftCount,
      toolId: unit.toolId,
    })),
  }))
}

interface PrintTool {
  name?: string
  description?: string | null
  isDefault?: boolean
  toolWorkPrice?: number | null
}

export const buildPrintName = (
  modelTypeLabel: string | undefined,
  modelName: string,
  tool?: PrintTool,
): string =>
  [modelTypeLabel, modelName, tool && !tool.isDefault ? tool.name : undefined]
    .filter(Boolean)
    .join(' ')

export const buildPrintCharacteristics = (
  equipmentYear: number | undefined,
  modelDescription: string | undefined,
  tool?: PrintTool,
): string =>
  [
    equipmentYear ? `Год выпуска ${equipmentYear}` : undefined,
    modelDescription?.trim(),
    tool?.description?.trim(),
  ]
    .filter(Boolean)
    .join('\n')

interface EquipmentForPrint {
  imageUrl: string
  name: string
  typeLabel?: string
  modelName: string
  characteristics: string
  quantity: number
  priceWithVat: string
  priceWithoutVat: string
  route: string
  workHoursMonthShifts: number
  partDeliveryPrice: number
  shiftsCount: number
  detalization: boolean
  baseRentalCost: number
  accommodationPrice: number
  foodPrice: number
  personnelHouseholdServicesCost: number
  fuelPrice: number
  fuelConsumption: number
  personDeliveryPrice: number
  relocationCost: number | undefined
  units: OfferModelUnit[]
  dailyMedCheckCost: number
  toolName?: string
  toolWorkPrice?: number
  vatVisibility: VatVisibilityEnum
}

// Transform equipment for PDF printing
export const transformEquipmentForPrint = (
  calculatedEquipments: ModelCalculated[],
  formValues: OfferFormData,
  personnelHouseholdServicesCost: number,
  WORK_HOURS_PER_MONTH: number,
  vatRate = 22,
): EquipmentForPrint[] => {
  return calculatedEquipments.flatMap((item) => {
    const createPrintItem = (
      units: OfferModelUnit[],
      quantity: number,
      totalRentPricePerHour: number | undefined,
      shiftsCount: number,
      tool?: PrintTool,
    ): EquipmentForPrint => ({
      imageUrl: item.imgBase63,
      name: buildPrintName(item?.modelType?.label, item.name, tool),
      typeLabel: item?.modelType?.label,
      modelName: buildPrintName(undefined, item.name, tool),
      characteristics: buildPrintCharacteristics(
        item.newestEquipmentYear,
        item.description,
        tool,
      ),
      quantity,
      priceWithVat: totalRentPricePerHour?.toFixed(0) || String(0),
      priceWithoutVat: totalRentPricePerHour
        ? subtractVatFromPrice(totalRentPricePerHour, vatRate).toFixed(0)
        : String(0),
      route: item.route || '',
      workHoursMonthShifts: shiftsCount * WORK_HOURS_PER_MONTH,
      partDeliveryPrice: formValues.partDeliveryPrice,
      shiftsCount,
      detalization: Boolean(item.detalization),
      baseRentalCost: resolveRentPrice(item.specialPrice, item.baseRentalCost),
      accommodationPrice: formValues.accommodationPrice || 0,
      foodPrice: formValues.foodPrice || 0,
      personnelHouseholdServicesCost:
        Number(personnelHouseholdServicesCost) || 0,
      fuelPrice: formValues.fuelPrice,
      fuelConsumption:
        (formValues.season === 'winter'
          ? item.fuelEfficiencyWinter
          : item.fuelEfficiencySummer) || 0,
      personDeliveryPrice: formValues.personDeliveryPrice,
      relocationCost: units[0]?.relocationCost ?? item.relocationCost,
      units,
      dailyMedCheckCost: formValues.dailyMedCheckCost || 0,
      toolName: tool?.name,
      toolWorkPrice: tool?.toolWorkPrice || 0,
      vatVisibility: formValues.vatVisibility,
    })

    if (!item.units?.length) {
      return [
        createPrintItem(
          [],
          item.count,
          item.totalRentPricePerHour,
          item.shiftsCount || 1,
        ),
      ]
    }

    const unitGroups = new Map<
      string,
      {
        units: OfferModelUnit[]
        toolId?: number
        shiftsCount: number
        totalRentPricePerHour: number
      }
    >()

    item.units.forEach((unit) => {
      const toolId = unit.toolId ?? unit.tool?.id
      const shiftsCount = unit.shiftCount || item.shiftsCount || 1
      const totalRentPricePerHour =
        unit.totalRentPricePerHour ??
        (item.units?.length === 1 ? item.totalRentPricePerHour : undefined) ??
        0
      const groupKey = JSON.stringify([
        toolId ?? null,
        shiftsCount,
        totalRentPricePerHour,
      ])
      const group = unitGroups.get(groupKey)

      if (group) {
        group.units.push(unit)
        return
      }

      unitGroups.set(groupKey, {
        units: [unit],
        toolId,
        shiftsCount,
        totalRentPricePerHour,
      })
    })

    return Array.from(unitGroups.values()).map((group) => {
      const modelTool = item.modelTools?.find(
        (candidate) => candidate.id === group.toolId,
      )
      const tool = modelTool ?? group.units[0]?.tool

      return createPrintItem(
        group.units,
        getUnitsQuantity(group.units),
        group.totalRentPricePerHour,
        group.shiftsCount,
        tool,
      )
    })
  })
}
